/**
 * Arcade / Chill tuning data for the gameplay core.
 *
 * Milestone 2 ships Arcade only; Chill arrives in milestone 3.
 * See docs/PLAN.md §4.10.
 */

export interface RunConfig {
  readonly id: 'arcade' | 'chill';
  /** Person restlessness rise per second at run start (before purr dampening). */
  readonly personBaseRatePerSecond: number;
  /** How much purr field (0..1) subtracts from person rise rate. */
  readonly purrDampeningPerSecond: number;
  /** Person restlessness level that triggers a wake event. */
  readonly personWakeThreshold: number;
  /** Seconds of telegraph before wake animation ends the run. */
  readonly wakeTelegraphSeconds: number;
  /** Pressure ratio above which sustained load adds cat restlessness. */
  readonly pressureRestlessnessThreshold: number;
  /** Scale for quadratic pressure-driven cat restlessness per second. */
  readonly pressureRestlessnessGainPerSecond: number;
  /** One-shot cat restlessness per unit of impact impulse. */
  readonly impactRestlessnessPerImpulse: number;
  /** Cat restlessness above this level blocks purr contribution. */
  readonly purrRestlessnessCap: number;
  /** Cat pressure ratio must stay below this to purr. */
  readonly purrPressureCap: number;
  /** Starting treat count in the pantry (milestone 2 vertical slice). */
  readonly startingTreats: number;
  /** Starting catnip uses in the pantry. */
  readonly startingCatnip: number;
  /** Restlessness removed per treat (before petAffinity). */
  readonly treatCalmAmount: number;
  /** Catnip area radius in world units. */
  readonly catnipRadius: number;
  /** Catnip calm multiplier vs treat (canonical 2×). */
  readonly catnipCalmMultiplier: number;
  /** Restlessness removed per registered stroke (before petAffinity). */
  readonly strokeCalmAmount: number;
  /** Restlessness calm per second during comfort hold (before petAffinity). */
  readonly holdCalmPerSecond: number;
  /** Bliss gained per unit of restlessness calm applied. */
  readonly blissPerCalmUnit: number;
  /** Seconds of comfort hold past bliss ≥ 1 before coax-down. */
  readonly coaxHoldDurationSeconds: number;
  /** Min pointer travel per stroke as fraction of cat long axis. */
  readonly minStrokeDistanceRatio: number;
  /** Min ms between registered strokes on one cat. */
  readonly minStrokeIntervalMs: number;
  /** Pointer movement below this (px) counts as comfort hold, not stroke. */
  readonly holdMotionThresholdPx: number;
  /** Ms stationary on cat before comfort hold engages (stroke cats). */
  readonly holdEngageDelayMs: number;
  /** Max treats from shelf pickups per run. */
  readonly maxTreats: number;
  /** Top N cats affected by toy visibility. */
  readonly toyVisibilityCatCount: number;
  readonly toyCuriousDurationSeconds: number;
  readonly toyShiftingDurationSeconds: number;
  readonly toyCuriousRestlessnessPerSecond: number;
  readonly toyShiftingRestlessnessPerSecond: number;
  readonly toyShiftingPersonRateBonus: number;
  readonly toyCollapsePersonSpike: number;
  readonly toyDefuseRestlessnessPerSecond: number;
  /** Soothing multiplier on distracted cats (0.5 = half). */
  readonly toyDistractedSoothingMultiplier: number;
  /** Waiting cats visible at once (Arcade = 2). */
  readonly queueVisibleCount: number;
  readonly queuePatiencePerSecond: number;
  readonly boredPatienceReset: number;
  readonly boredWalkTelegraphSeconds: number;
  readonly boredBatDurationSeconds: number;
  readonly boredBatPersonRatePerSecond: number;
  readonly boredYowlDurationSeconds: number;
  readonly boredYowlPersonRatePerSecond: number;
  readonly boredYowlStackRatePerSecond: number;
  readonly boredPlayToyDurationSeconds: number;
  readonly boredKneadDurationSeconds: number;
  readonly spawnWeightHeightStart: number;
  readonly spawnWeightHeightRamp: number;
  readonly spawnWeightCountStart: number;
  readonly spawnWeightCountRamp: number;
  readonly spawnAntiRepeatUntilPlaced: number;
  readonly spawnArchetypeBias: Readonly<
    Record<string, { readonly early: number; readonly late: number }>
  >;
}

/** Default Arcade tuning — first pass for the vertical slice. */
export const ARCADE_RUN_CONFIG: RunConfig = {
  id: 'arcade',
  personBaseRatePerSecond: 0.011,
  purrDampeningPerSecond: 0.009,
  personWakeThreshold: 1,
  wakeTelegraphSeconds: 2.5,
  pressureRestlessnessThreshold: 0.7,
  pressureRestlessnessGainPerSecond: 0.35,
  impactRestlessnessPerImpulse: 0.004,
  purrRestlessnessCap: 0.45,
  purrPressureCap: 0.75,
  startingTreats: 8,
  startingCatnip: 3,
  treatCalmAmount: 0.35,
  catnipRadius: 90,
  catnipCalmMultiplier: 2,
  strokeCalmAmount: 0.12,
  holdCalmPerSecond: 0.22,
  blissPerCalmUnit: 0.35,
  coaxHoldDurationSeconds: 1.5,
  minStrokeDistanceRatio: 0.6,
  minStrokeIntervalMs: 200,
  holdMotionThresholdPx: 10,
  holdEngageDelayMs: 120,
  maxTreats: 12,
  toyVisibilityCatCount: 3,
  toyCuriousDurationSeconds: 4,
  toyShiftingDurationSeconds: 3,
  toyCuriousRestlessnessPerSecond: 0.08,
  toyShiftingRestlessnessPerSecond: 0.12,
  toyShiftingPersonRateBonus: 0.006,
  toyCollapsePersonSpike: 0.18,
  toyDefuseRestlessnessPerSecond: 0.15,
  toyDistractedSoothingMultiplier: 0.5,
  queueVisibleCount: 2,
  queuePatiencePerSecond: 0.045,
  boredPatienceReset: 0.25,
  boredWalkTelegraphSeconds: 1.5,
  boredBatDurationSeconds: 3,
  boredBatPersonRatePerSecond: 0.025,
  boredYowlDurationSeconds: 2.5,
  boredYowlPersonRatePerSecond: 0.012,
  boredYowlStackRatePerSecond: 0.06,
  boredPlayToyDurationSeconds: 1.2,
  boredKneadDurationSeconds: 3,
  spawnWeightHeightStart: 60,
  spawnWeightHeightRamp: 120,
  spawnWeightCountStart: 4,
  spawnWeightCountRamp: 8,
  spawnAntiRepeatUntilPlaced: 6,
  spawnArchetypeBias: {
    loaf_orange: { early: 1.4, late: 0.9 },
    kitten_gray: { early: 1.2, late: 0.7 },
    hefty_calico: { early: 0.5, late: 1.5 },
  },
};
